import { FC, useEffect, useState } from 'react';
import {
  IdeaAccessibility,
  IdeaCategoryId,
  IdeaRecord,
  IdeaStatusId,
  IdeaUpdateRequest,
} from '../../api/api';
import { Button, CopyableInput, Input, ISelectOptionProp, Select, TextArea } from '../../components/Form';

export const PAGE_SIZE = 20;
export const VOTES_PAGE_SIZE = 20;

export const STATUS_LABELS: Record<IdeaStatusId, string> = {
  1: 'На модерации',
  2: 'На голосовании',
  3: 'В разработке',
  4: 'Реализована',
  5: 'Отменена',
};

export const STATUS_COLORS: Record<IdeaStatusId, string> = {
  1: 'bg-amber-100 text-amber-800',
  2: 'bg-sky-100 text-sky-800',
  3: 'bg-indigo-100 text-indigo-800',
  4: 'bg-emerald-100 text-emerald-800',
  5: 'bg-rose-100 text-rose-800',
};

export const CATEGORY_LABELS: Record<IdeaCategoryId, string> = {
  scenario_creation: 'Создание сценариев',
  scenario_access: 'Доступ к сценариям',
  roleplay_passing: 'Прохождение ролевых игр',
  results_and_reports: 'Результаты и отчеты',
  other: 'Другое',
};

export const ACCESSIBILITY_LABELS: Record<IdeaAccessibility, string> = {
  public: 'Публичная',
  private: 'Приватная',
};

export const STATUS_OPTIONS: ISelectOptionProp[] = Object.entries(STATUS_LABELS).map(([id, name]) => ({
  _id: id,
  name,
}));

export const CATEGORY_OPTIONS: ISelectOptionProp[] = Object.entries(CATEGORY_LABELS).map(([id, name]) => ({
  _id: id,
  name,
}));

export const ACCESSIBILITY_OPTIONS: ISelectOptionProp[] = Object.entries(ACCESSIBILITY_LABELS).map(([id, name]) => ({
  _id: id,
  name,
}));

export const ORDER_OPTIONS: ISelectOptionProp[] = [
  { _id: 'UPDATED_DESC', name: 'Сначала недавно обновленные' },
  { _id: 'CREATED_DESC', name: 'Сначала новые' },
  { _id: 'CREATED_ASC', name: 'Сначала старые' },
  { _id: 'TITLE_ASC', name: 'По названию А-Я' },
  { _id: 'RANDOM', name: 'Случайный порядок' },
];

interface IdeaEditorFormState {
  title: string;
  description: string;
  dev_comment: string;
  note: string;
  costs: string;
  status_id: IdeaStatusId;
  category_id: IdeaCategoryId | '';
  accessibility_id: IdeaAccessibility;
}

export const formatDateTime = (value: string | null | undefined) => {
  if (!value) {
    return '—';
  }

  return new Intl.DateTimeFormat('ru-RU', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
};

export const formatCriticality = (value: number | null | undefined) => {
  if (value === null || value === undefined) {
    return '—';
  }

  if (value >= 2.5) {
    return 'Критичная';
  }

  if (value >= 1.5) {
    return 'Средняя';
  }

  return 'Низкая';
};

export const getErrorMessage = (error: unknown) => {
  if (!error || typeof error !== 'object') {
    return 'Не удалось выполнить запрос.';
  }

  if ('status' in error) {
    const data = (error as { data?: { detail?: string } }).data;
    if (typeof data?.detail === 'string' && data.detail) {
      return data.detail;
    }
  }

  if ('message' in error && typeof (error as { message?: string }).message === 'string') {
    return (error as { message: string }).message;
  }

  return 'Не удалось выполнить запрос.';
};

const createFormState = (idea: IdeaRecord): IdeaEditorFormState => ({
  title: idea.title,
  description: idea.description,
  dev_comment: idea.dev_comment ?? '',
  note: idea.note ?? '',
  costs: idea.costs === null ? '' : String(idea.costs),
  status_id: idea.status_id,
  category_id: idea.category_id ?? '',
  accessibility_id: idea.accessibility_id,
});

export const IdeaEditorForm: FC<{
  idea: IdeaRecord;
  organizationName: string;
  authorName: string;
  onCancel: () => void;
  onDelete: () => Promise<void>;
  onSubmit: (payload: IdeaUpdateRequest) => Promise<void>;
  isSaving: boolean;
  isDeleting: boolean;
  errorMessage: string | null;
}> = ({
  idea,
  organizationName,
  authorName,
  onCancel,
  onDelete,
  onSubmit,
  isSaving,
  isDeleting,
  errorMessage,
}) => {
  const [formState, setFormState] = useState<IdeaEditorFormState>(() => createFormState(idea));

  useEffect(() => {
    setFormState(createFormState(idea));
  }, [idea]);

  const isSubmitDisabled = !formState.title.trim() || !formState.description.trim() || isSaving || isDeleting;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    await onSubmit({
      title: formState.title.trim(),
      description: formState.description.trim(),
      dev_comment: formState.dev_comment.trim() || null,
      note: formState.note.trim() || null,
      costs: formState.costs.trim() === '' ? null : Number(formState.costs),
      status_id: formState.status_id,
      category_id: formState.category_id || null,
      accessibility_id: formState.accessibility_id,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <CopyableInput label="ID" value={idea.id} />
      <Input label="Организация" value={organizationName} readOnly />
      <Input label="Автор" value={authorName} readOnly />

      <div className="grid grid-cols-2 gap-4">
        <Input label="Создана" value={formatDateTime(idea.created_at)} readOnly />
        <Input label="Обновлена" value={formatDateTime(idea.updated_at)} readOnly />
      </div>

      <div className="grid grid-cols-3 gap-4">
        <Input label="Голосов" value={String(idea.votes_count ?? 0)} readOnly />
        <Input label="Критичность" value={formatCriticality(idea.avg_criticality)} readOnly />
        <Input label="Голосов в организации" value={String(idea.organization_votes_count ?? 0)} readOnly />
      </div>

      <Input
        label="Название"
        value={formState.title}
        onChange={(event) => setFormState((prev) => ({ ...prev, title: event.target.value }))}
        required
      />

      <TextArea
        label="Описание"
        rows={5}
        value={formState.description}
        onChange={(event) => setFormState((prev) => ({ ...prev, description: event.target.value }))}
        required
      />

      <div className="grid grid-cols-2 gap-4">
        <Select
          label="Статус"
          value={String(formState.status_id)}
          onChange={(event) =>
            setFormState((prev) => ({ ...prev, status_id: Number(event.target.value) as IdeaStatusId }))
          }
          options={STATUS_OPTIONS}
          showEmptyOption={false}
        />
        <Select
          label="Категория"
          value={formState.category_id}
          onChange={(event) =>
            setFormState((prev) => ({ ...prev, category_id: event.target.value as IdeaCategoryId | '' }))
          }
          options={CATEGORY_OPTIONS}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Select
          label="Доступность"
          value={formState.accessibility_id}
          onChange={(event) =>
            setFormState((prev) => ({ ...prev, accessibility_id: event.target.value as IdeaAccessibility }))
          }
          options={ACCESSIBILITY_OPTIONS}
          showEmptyOption={false}
        />
        <Input
          label="Затраты"
          type="number"
          min={0}
          value={formState.costs}
          onChange={(event) => setFormState((prev) => ({ ...prev, costs: event.target.value }))}
        />
      </div>

      <Input
        label="Комментарий разработчика"
        value={formState.dev_comment}
        onChange={(event) => setFormState((prev) => ({ ...prev, dev_comment: event.target.value }))}
      />

      <TextArea
        label="Заметка"
        rows={4}
        value={formState.note}
        onChange={(event) => setFormState((prev) => ({ ...prev, note: event.target.value }))}
      />

      {errorMessage ? (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {errorMessage}
        </div>
      ) : null}

      <div className="flex items-center justify-between gap-2 border-t border-gray-200 pt-4">
        <Button
          type="button"
          onClick={() => {
            void onDelete();
          }}
          className="bg-red-600 hover:bg-red-700 disabled:bg-red-300"
          disabled={isSaving || isDeleting}
        >
          Удалить
        </Button>
        <div className="flex gap-2">
          <Button type="button" onClick={onCancel} className="bg-gray-600 hover:bg-gray-700" disabled={isSaving || isDeleting}>
            Отмена
          </Button>
          <Button type="submit" disabled={isSubmitDisabled}>
            {isSaving ? 'Сохранение...' : 'Сохранить'}
          </Button>
        </div>
      </div>
    </form>
  );
};
